import React, { FC, MouseEvent, useEffect, useState } from 'react';
import { formatDate, formatPatientName } from 'utils/format';
import { TableCell, TableRow, Box, IconButton, Typography } from '@mui/material';
import { Order, LinkedUser } from 'models/order';
import RestorationTypes from 'components/orders/shared/restoration-types.component';
import DeliveryDetails from 'components/orders/shared/delivery-details.component';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { OrderItemContext } from 'models/order-item-events';
import { useNavigate } from 'react-router-dom';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { useTranslation } from 'react-i18next';
import TimeInStep from 'components/shared/time-in-step/time-in-step.component';
import LabFlagButton from 'components/shared/lab-flag-button/lab-flag-button.component';
import { OrderTag } from 'models/tags';
import OrderListTagComponent from 'components/shared/order-list-tag/order-list-tag.component';
import OrderCheckBox from 'components/shared/order-check-box/order-check-box.component';
import { isSupplier } from 'utils/client-settings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { pausedLabel } from 'utils/paused-label';

type ExternalWorkflowOrderItemProps = {
    order: Order;
    hidePatientDetails?: boolean;
    onOrderContextMenu: (orderContext: OrderItemContext) => void;
    batchActionSelected?: boolean;
    onHandleCheckedOrders?: (order: Order, checked: boolean, orderIndex: number) => void;
    orderIndex: number;
    isAtLimit?: boolean;
    isChecked?: boolean;
};
const ExternalWorkflowOrderItem: FC<ExternalWorkflowOrderItemProps> = ({
    order,
    onOrderContextMenu,
    hidePatientDetails,
    batchActionSelected,
    onHandleCheckedOrders,
    orderIndex,
    isAtLimit,
    isChecked,
}: ExternalWorkflowOrderItemProps) => {
    const { t: translate } = useTranslation(['workflow', 'orders']);
    const navigate = useNavigate();
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const [linkedTech, setLinkedTech] = useState<LinkedUser | undefined>({} as LinkedUser);
    const [orderTags, setOrderTags] = useState<OrderTag[]>();

    useEffect(() => {
        setOrderTags(order.orderTags);
    }, [order.orderTags]);

    const navigateToOrderDetails = () => {
        navigate(`/order-details/${order._id}/main-view`);
    };

    const handleContextMenu = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onOrderContextMenu({ order: order, mouseX: event.clientX, mouseY: event.clientY });
    };

    useEffect(() => {
        if (order?.linkedExtTechs) {
            const user = order.linkedExtTechs.find(
                (linked) => linked.user?._id?.toString() === currentUser?._id?.toString(),
            );
            setLinkedTech(user);
        }
    }, [order.linkedExtTechs]);

    const updateOrderTag = (tags: OrderTag[]) => {
        setOrderTags(tags);
    };

    return (
        <TableRow
            sx={{
                cursor: 'pointer',
                borderLeft: `4px solid ${orderTags?.length === 1 ? orderTags[0].color : '#fff'}`,
            }}
            hover
            onClick={() => {
                navigateToOrderDetails();
            }}
            onContextMenu={handleContextMenu}
        >
            {batchActionSelected ? (
                <TableCell sx={{ padding: 0 }}>
                    <OrderCheckBox
                        order={order}
                        isAtLimit={isAtLimit}
                        checked={isChecked ?? false}
                        onHandleCheckedOrders={
                            onHandleCheckedOrders &&
                            ((checkedOrder, checked) => onHandleCheckedOrders(checkedOrder, checked, orderIndex))
                        }
                    />
                </TableCell>
            ) : (
                <TableCell sx={{ padding: 0 }}>
                    {isSupplier(currentOrganization._id, order.supplier._id) ? (
                        <LabFlagButton order={order} />
                    ) : undefined}
                </TableCell>
            )}
            <TableCell>
                {orderTags ? (
                    <OrderListTagComponent updateOrderTag={updateOrderTag} orderId={order._id} orderTags={orderTags} />
                ) : (
                    <span>-</span>
                )}
            </TableCell>
            <TableCell>
                <TimeInStep
                    timeSpent={order.workFlow?.timeInCurrentStep}
                    maxHourInStep={order.workFlow?.currentStep.maxHourInStep}
                />
                {order.workFlow?.currentStep?.dateTime && (
                    <Typography variant="caption" component="div" color="grey.500">
                        {formatDate(new Date(order.workFlow?.currentStep?.dateTime), 'datetime')}
                    </Typography>
                )}
                {order.workFlow?.currentStep?.isPaused ? (
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'warning.main',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                        title={pausedLabel(order.workFlow?.currentStep?.pauseReason, translate)}
                    >
                        {pausedLabel(order.workFlow?.currentStep?.pauseReason, translate)}
                    </Typography>
                ) : undefined}
            </TableCell>
            <TableCell>
                <Box>{order.orderId}</Box>
                {order.internalOrderId ? <Typography variant="caption">{order.internalOrderId}</Typography> : undefined}
            </TableCell>

            {!hidePatientDetails ? (
                <TableCell sx={{ maxWidth: 200 }}>
                    <Box>{formatPatientName(order.patient, translate('orders:NO_PATIENT_REFERENCE'))}</Box>
                </TableCell>
            ) : undefined}
            <TableCell sx={{ maxWidth: 200 }}>
                <RestorationTypes sampleType={order.deliveryDetails.sampleType} types={order.restorationTypes} />
            </TableCell>
            <TableCell sx={{ maxWidth: 200 }}>
                {order.constructionMaterials?.map((material, index) => <Box key={index}>{material}</Box>)}
            </TableCell>
            <TableCell sx={{ minWidth: 125, maxWidth: 140 }}>
                <Box>{formatDate(new Date(linkedTech?.dateAdded || ''), 'date')}</Box>
            </TableCell>
            <TableCell sx={{ width: 115 }}>
                <DeliveryDetails details={order.deliveryDetails} />
            </TableCell>
            <TableCell>
                {order.deliveryDetails.externalDeliveryDate ? (
                    <Box>{formatDate(new Date(order.deliveryDetails.externalDeliveryDate), 'date')}</Box>
                ) : (
                    <span>-</span>
                )}
            </TableCell>
            <TableCell sx={{ maxWidth: '30px', paddingLeft: 0 }}>
                <IconButton size="small" onClick={handleContextMenu}>
                    <MoreVertIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default ExternalWorkflowOrderItem;
